// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { OrderItem } from '../cache/OrderItem';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { TradingButtonItem } from '../elements/TradingButtonStripe';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { OrdersPanelBase } from './OrdersPanelBase';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { OrderActionEnum } from '@shared/utils/Trading/OrderActionEnum';
import { NumericLinks } from '@shared/commons/cache/OrderParams/order-edit/OrderEditBase';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { WorkingOrdersHotkeysEnum } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { OrderTypeColumnRenderer } from '../elements/QuickTable/Renderers/OrderTypeColumnRenderer';
import { TrailingColumnRenderer } from '../elements/QuickTable/Renderers/TrailingColumnRenderer';
export class OrdersPanel extends OrdersPanelBase {
    constructor() {
        super();
        this.Name = 'OrdersPanel';
        this.headerLocaleKey = 'panel.orders';
        this.moreThanOneTTKey = 'panel.orders.menu.MoreOneSymbolInfoDisabled.tt'; // #95439
        this.noOneTTKey = 'panel.orders.menu.NoSymbolInfoDisabled.tt';
    }
    // #region Overrides
    oninit() {
        super.oninit();
        this.processTableMouseDown = this.processTableMouseDown.bind(this);
    }
    oncomplete() {
        super.oncomplete();
        const quickTable = this.getQuickTable();
        if (!isNullOrUndefined(quickTable)) {
            const columns = quickTable.columns;
            columns[OrderItem.ORDER_TYPE_COL_INDEX].renderer = new OrderTypeColumnRenderer();
            columns[OrderItem.SL_PRICE_COL_INDEX].renderer = columns[OrderItem.SL_OFFSET_COL_INDEX].renderer =
                new TrailingColumnRenderer();
            quickTable.OnPaintedPictureButtonClick.Subscribe(this.CloseBtnClick, this);
            quickTable.onTableMouseDown.Subscribe(this.onTableMouseDown, this);
        }
    }
    // #region ApplicationPanelWithTable
    getType() {
        return PanelNames.OrdersPanel;
    }
    SetColumnsDefaultDisplayIndex(table) {
        table.columns[0].displayedIndex = 0;
        table.columns[1].displayedIndex = 1;
        table.columns[4].displayedIndex = 2;
        table.columns[11].displayedIndex = 3;
        table.columns[6].displayedIndex = 4;
        table.columns[7].displayedIndex = 5;
        table.columns[9].displayedIndex = 6;
        table.columns[5].displayedIndex = 7;
        table.columns[16].displayedIndex = 8;
        table.columns[35].displayedIndex = 9;
        table.columns[46].displayedIndex = 99999; // Close button column
    }
    SetColumnsColouringMode(table) {
        super.ProcessSetColumnsColouringMode(table, [8], ColouringModes.Previous);
    }
    populateItemsDirect() {
        super.populateItemsDirect();
        const orderDict = DataCache.Trading.getAllOrders();
        for (const order of orderDict) {
            this.AddOrderEvent(order);
        }
    }
    createMenuItems() {
        const actions = OrderActionEnum;
        const placedFromVal = PlacedFrom.WEB_ORDERS_PANEL_CONTEXT_MENU;
        const items = [
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.orders.menu.ModifyOrder',
                tag: actions.ModifyOrder,
                event: (item) => {
                    this.action(actions.ModifyOrder, true, placedFromVal, null);
                }
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.orders.menu.ExecuteAsMarket',
                tag: actions.ChangeToMarket,
                visible: !Resources.isHidden('panel.orders.menu.ExecuteAsMarket'),
                event: (item) => {
                    this.action(actions.ChangeToMarket, true, placedFromVal, null);
                }
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.orders.menu.Remove',
                tag: 'CloseGroup',
                svgImage: 'closeCrossBtn',
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.orders.menu.CancelOrder',
                        tag: actions.ByIds,
                        event: (item) => {
                            this.action(actions.ByIds, true, placedFromVal, null);
                        }
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.orders.menu.CancelAllByInstrument',
                        tag: actions.ByInstrument,
                        event: (item) => {
                            this.action(actions.ByInstrument, true, placedFromVal, null);
                        }
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.orders.menu.CancelAllForAccount',
                        tag: actions.ByAccount,
                        event: (item) => {
                            this.action(actions.ByAccount, true, placedFromVal, null);
                        }
                    },
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.orders.menu.CancelAllOrders',
                        tag: actions.All,
                        event: (item) => {
                            this.action(actions.All, true, placedFromVal, null);
                        }
                    }
                ]
            }
        ];
        // переделать в случае появления новых subitems
        if (!Resources.isHidden('panel.orders.menu.ShowToolbar')) {
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.View',
                tag: 'View',
                svgImage: 'View',
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.orders.menu.ShowToolbar',
                        tag: actions.ShowToolbar,
                        canCheck: true,
                        checked: this.ToolbarVisible,
                        event: (item) => {
                            this.ShowToolbarStateChange(item);
                        }
                    }
                ]
            });
        }
        this.NewAddOpeningPanelsCM(items);
        this.AddSymbolInfoContextMenuItemIfNeed(items);
        return items;
    }
    GetOpeningPanelsCMLocKeysSet() {
        const keysObj = {};
        keysObj[ApplicationPanelWithTable.OPEN_CHART] = 'panel.orders.showChartMI';
        keysObj[ApplicationPanelWithTable.OPEN_MD] = 'panel.orders.showMDMI';
        keysObj[ApplicationPanelWithTable.OPEN_TS] = 'panel.orders.showTSMI';
        keysObj[ApplicationPanelWithTable.OPEN_OE] = 'panel.orders.showOEMI';
        return keysObj;
    }
    // #endregion
    // #region OrderPanelBase
    DCEventsSubscribe() {
        DataCache.OnAddOrder.Subscribe(this.AddOrderEvent, this);
        DataCache.OnRemoveOrder.Subscribe(this.RemoveOrderEvent, this);
        DataCache.OnAddSLOrderToPosition.Subscribe(this.AddSLOrderToPositionEvent, this);
        DataCache.OnAddTPOrderToPosition.Subscribe(this.AddTPOrderToPositionEvent, this);
        DataCache.OnRemoveSLOrderFromPosition.Subscribe(this.RemoveSLOrderFromPositionEvent, this);
        DataCache.OnRemoveTPOrderFromPosition.Subscribe(this.RemoveTPOrderFromPositionEvent, this);
    }
    DCEventsUnSubscribe() {
        DataCache.OnAddOrder.UnSubscribe(this.AddOrderEvent, this);
        DataCache.OnRemoveOrder.UnSubscribe(this.RemoveOrderEvent, this);
        DataCache.OnAddSLOrderToPosition.UnSubscribe(this.AddSLOrderToPositionEvent, this);
        DataCache.OnAddTPOrderToPosition.UnSubscribe(this.AddTPOrderToPositionEvent, this);
        DataCache.OnRemoveSLOrderFromPosition.UnSubscribe(this.RemoveSLOrderFromPositionEvent, this);
        DataCache.OnRemoveTPOrderFromPosition.UnSubscribe(this.RemoveTPOrderFromPositionEvent, this);
    }
    getDoubleClickActionParams() {
        return {
            action: OrderActionEnum.ByIds,
            confirm: true,
            placedFrom: PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK
        };
    }
    getHotButtonPlacedFrom() {
        return PlacedFrom.WEB_ORDERS_PANEL_HOT_BTN;
    }
    CloseBtnClick(args) {
        const selectedOrderIdArr = [args.row.id];
        if (this.IsClosingForbidden(selectedOrderIdArr)) {
            return;
        }
        void DataCache.FOrderExecutor.orderAction(OrderActionEnum.ByIds, this.isFiltered, selectedOrderIdArr, selectedOrderIdArr, true, PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK, undefined);
    }
    IsClosingAllowed(selectedOrderIdArr) {
        const orders = DataCache.Trading.getOrdersById(selectedOrderIdArr);
        let result = null;
        for (const order of orders) {
            const res = IsAllowed.IsOrderCancelingAllowed(order);
            if (result === null || !res.Allowed) {
                result = res;
            }
        }
        return result;
    }
    createTradingButtonItems() {
        return [
            new TradingButtonItem(OrderActionEnum.All, 'panels.orders.buttons.CancelAllButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.All)),
            new TradingButtonItem(OrderActionEnum.Sell, 'panels.orders.buttons.CancelSellButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.Sell)),
            new TradingButtonItem(OrderActionEnum.Buy, 'panels.orders.buttons.CancelBuyButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.Buy)),
            new TradingButtonItem(OrderActionEnum.Stops, 'panels.orders.buttons.CancelStopsButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.Stops)),
            new TradingButtonItem(OrderActionEnum.Limits, 'panels.orders.buttons.CancelLimitsButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.Limits)),
            new TradingButtonItem(OrderActionEnum.Days, 'panels.orders.buttons.CancelDayButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.Days)),
            new TradingButtonItem(OrderActionEnum.GTCs, 'panels.orders.buttons.CancelGTCButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.GTCs)),
            new TradingButtonItem(OrderActionEnum.ByIds, 'panels.orders.buttons.CancelSelectedButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.ByIds)),
            new TradingButtonItem(OrderActionEnum.ByAccountAndInstrument, 'panels.orders.buttons.CancelByInstrumentButton', this.isFiltered && this.isFilteredAction(OrderActionEnum.ByAccountAndInstrument)),
            new TradingButtonItem(OrderActionEnum.ModifyOrder, 'panels.orders.buttons.ModifyOrder', this.isFiltered && this.isFilteredAction(OrderActionEnum.ModifyOrder)),
            new TradingButtonItem(OrderActionEnum.ChangeToMarket, 'panels.orders.buttons.AsMarket', this.isFiltered && this.isFilteredAction(OrderActionEnum.ChangeToMarket))
        ];
    }
    isFilteredAction(action) {
        switch (action) {
            case OrderActionEnum.ByIds:
            case OrderActionEnum.ByAccountAndInstrument:
            case OrderActionEnum.ModifyOrder:
            case OrderActionEnum.ChangeToMarket:
                return false;
            default:
                return true;
        }
    }
    // TODO. Rename.
    action(action, confirm, placedFrom, columnIndexToActivate) {
        if (!super.action(action, confirm, placedFrom, columnIndexToActivate)) {
            return false;
        }
        const quickTable = this.getQuickTable();
        const selectedRowsIds = quickTable.selectedRowIds;
        const allRowsId = quickTable.getTableItemsIds();
        void DataCache.FOrderExecutor.orderAction(action, this.isFiltered, selectedRowsIds, allRowsId, confirm, placedFrom, columnIndexToActivate);
        return true;
    }
    updateAllowedActions() {
        if (isNullOrUndefined(this.Controls) || isNullOrUndefined(this.quickTableRactive)) {
            return;
        }
        const menuTagDict = this.menuTagDict;
        if (isNullOrUndefined(menuTagDict))
            return;
        const quickTable = this.getQuickTable();
        if (isNullOrUndefined(quickTable))
            return;
        const btns = this.Controls.btns;
        if (isNullOrUndefined(btns))
            return;
        const actions = OrderActionEnum;
        const selectedOrderIdArr = quickTable.selectedRowIds;
        const row = quickTable.rows[selectedOrderIdArr[0]];
        const allRowsIds = quickTable.getTableItemsIds();
        const allowedActionSet = DataCache.FOrderExecutor.getAllowedOrderActionSet(selectedOrderIdArr, allRowsIds, this.isFiltered);
        const ins = !isNullOrUndefined(row) ? row.item.DisplayInstrumentName() : '';
        btns.updateEnability(allowedActionSet);
        btns.setInstrument(actions.ByAccountAndInstrument, ins);
        const changeToMarket = allowedActionSet[actions.ChangeToMarket];
        const modifyOrder = allowedActionSet[actions.ModifyOrder];
        const cancelAll = allowedActionSet[actions.All];
        const cancelByIds = allowedActionSet[actions.ByIds];
        const cancelByInstrument = allowedActionSet[actions.ByInstrument];
        const cancelByAccount = allowedActionSet[actions.ByAccount];
        menuTagDict[actions.ChangeToMarket].enabled = changeToMarket !== undefined && changeToMarket.Allowed;
        menuTagDict[actions.ModifyOrder].enabled = modifyOrder !== undefined && modifyOrder.Allowed;
        menuTagDict.CloseGroup.enabled =
            cancelAll !== undefined ||
                cancelByInstrument !== undefined ||
                cancelByAccount !== undefined ||
                cancelByIds !== undefined;
        menuTagDict[actions.All].enabled = cancelAll !== undefined && cancelAll.Allowed;
        menuTagDict[actions.ByIds].enabled = cancelByIds !== undefined && (cancelByIds === null || cancelByIds === void 0 ? void 0 : cancelByIds.Allowed);
        menuTagDict[actions.ByInstrument].enabled = cancelByInstrument !== undefined && (cancelByInstrument === null || cancelByInstrument === void 0 ? void 0 : cancelByInstrument.Allowed);
        menuTagDict[actions.ByAccount].enabled = cancelByAccount !== undefined && (cancelByAccount === null || cancelByAccount === void 0 ? void 0 : cancelByAccount.Allowed);
        const enabled = !!ins;
        this.SetOpeningPanelsCMEnability(menuTagDict, enabled);
    }
    isClosingAction(action) {
        const closingActions = [
            OrderActionEnum.All,
            OrderActionEnum.ByIds,
            OrderActionEnum.Buy,
            OrderActionEnum.Sell,
            OrderActionEnum.ByInstrument,
            OrderActionEnum.ByAccount,
            OrderActionEnum.ByAccountAndInstrument
        ];
        return closingActions.includes(action);
    }
    // #endregion
    // #endregion
    onTableMouseDown(hittestInfo) {
        if (!isNullOrUndefined(this.doubleClickedTimerID)) {
            clearTimeout(this.doubleClickedTimerID);
        }
        this.doubleClickedTimerID = setTimeout(() => {
            this.processTableMouseDown(hittestInfo);
        }, 200);
    }
    processTableMouseDown(hittestInfo) {
        const quickTable = this.getQuickTable();
        if (isNullOrUndefined(quickTable) ||
            isNullOrUndefined(hittestInfo) ||
            !isValidNumber(hittestInfo.columnIndex) ||
            hittestInfo.columnIndex === -1) {
            return;
        }
        const columnIndex = quickTable.sortedColumns[hittestInfo.columnIndex].PRIVATE.index;
        const selectedItem = quickTable.getSelectedTableItems()[0];
        if (isNullOrUndefined(selectedItem) || !selectedItem.isAllowEdit(columnIndex)) {
            return;
        }
        const action = OrderActionEnum.ModifyOrder;
        const placedFrom = PlacedFrom.WEB_ORDERS_PANEL_DB_CLICK;
        switch (columnIndex) {
            case OrderItem.QUANTITY_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.Quantity);
                break;
            case OrderItem.PRICE_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.LimitPrice);
                break;
            case OrderItem.STOP_PRICE_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.StopPrice);
                break;
            case OrderItem.SL_PRICE_COL_INDEX:
            case OrderItem.SL_OFFSET_COL_INDEX:
            case OrderItem.SLL_PRICE_COL_INDEX:
            case OrderItem.SLL_OFFSET_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.StopLoss);
                break;
            case OrderItem.TP_PRICE_COL_INDEX:
            case OrderItem.TP_OFFSET_COL_INDEX:
                this.action(action, true, placedFrom, NumericLinks.TakeProfit);
                break;
        }
    }
    AddOrderEvent(order) {
        this.getQuickTable().AddItem(new OrderItem(order, SessionSettings));
    }
    AddSLOrderToPositionEvent(position) {
        if (isNullOrUndefined(position) || isNullOrUndefined(position.SLOrder)) {
            return;
        }
        this.AddOrderEvent(position.SLOrder);
    }
    AddTPOrderToPositionEvent(position) {
        if (isNullOrUndefined(position) || isNullOrUndefined(position.TPOrder)) {
            return;
        }
        this.AddOrderEvent(position.TPOrder);
    }
    RemoveOrderEvent(order) {
        this.getQuickTable().RemoveItem(OrderItem.GetItemId(order));
    }
    RemoveSLOrderFromPositionEvent(position) {
        if (isNullOrUndefined(position) || isNullOrUndefined(position.SLOrder)) {
            return;
        }
        this.RemoveOrderEvent(position.SLOrder);
    }
    RemoveTPOrderFromPositionEvent(position) {
        if (isNullOrUndefined(position) || isNullOrUndefined(position.TPOrder)) {
            return;
        }
        this.RemoveOrderEvent(position.TPOrder);
    }
    HotkeyPressed(hotkey) {
        const isPressed = super.HotkeyPressed(hotkey);
        if (isPressed) {
            return true;
        }
        const placedFromVal = PlacedFrom.WEB_ORDERS_PANEL_HOT_BTN;
        const hotkeyAction = HotkeysManager.GetActionByHotkeyForPanel(hotkey, this.getType());
        switch (hotkeyAction) {
            case WorkingOrdersHotkeysEnum.ModifyOrder:
                this.action(OrderActionEnum.ModifyOrder, true, placedFromVal, null);
                return true;
            case WorkingOrdersHotkeysEnum.CancelOrder:
                this.action(OrderActionEnum.ByIds, true, placedFromVal, null);
                return true;
            case WorkingOrdersHotkeysEnum.ChangeToMarket:
                this.action(OrderActionEnum.ChangeToMarket, true, placedFromVal, null);
                return true;
        }
        return false;
    }
}
